import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Input, Button, Badge, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../../lib/apiClient.js';

// Platform promo codes (architecture.md §24 M8, M10). Super Admin defines coupon discounts
// shared with hosts/clients. A promo is either appointment-scoped (applicable_to) or scoped
// to a specific plan's upgrade (plan_id). Applied at checkout (post-MVP).

const EMPTY = {
  code: '', discount_type: 'percentage', discount_value: '',
  max_uses: '', applicable: 'all', expires_at: '',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function discountLabel(p) {
  return p.discount_type === 'percentage'
    ? `${p.discount_value}%`
    : `₹${(Number(p.discount_value) / 100).toFixed(2)}`;
}

function applicableLabel(p) {
  if (p.plan_id) return `${p.plan_name || 'Plan'} Upgrade`;
  return p.applicable_to === 'paid_appointments_only' ? 'Paid appointments only' : 'All appointments';
}

function statusOf(p) {
  if (p.is_disabled) return { label: 'Disabled', tone: 'danger' };
  if (p.expires_at && new Date(p.expires_at) <= new Date()) return { label: 'Expired', tone: 'neutral' };
  if (p.max_uses != null && p.used_count >= p.max_uses) return { label: 'Exhausted', tone: 'warning' };
  return { label: 'Active', tone: 'success' };
}

function PromoRow({ p }) {
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-promos'] });
  const toggle = useMutation({ mutationFn: () => api.adminUpdatePlatformPromo(p.id, !p.is_disabled), onSuccess: invalidate });
  const del = useMutation({ mutationFn: () => api.adminDeletePlatformPromo(p.id), onSuccess: invalidate });
  const st = statusOf(p);

  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-3 font-mono font-medium text-slate-900">{p.code}</td>
      <td className="px-4 py-3 text-slate-700">{discountLabel(p)}</td>
      <td className="px-4 py-3 text-slate-700">{applicableLabel(p)}</td>
      <td className="px-4 py-3"><Badge tone={st.tone}>{st.label}</Badge></td>
      <td className="px-4 py-3 text-slate-600">{p.used_count}{p.max_uses != null ? ` / ${p.max_uses}` : ' / ∞'}</td>
      <td className="px-4 py-3 text-slate-600">{fmtDate(p.expires_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
            {p.is_disabled ? 'Enable' : 'Disable'}
          </Button>
          {confirmDel ? (
            <>
              <Button variant="danger" onClick={() => del.mutate()} disabled={del.isPending}>{del.isPending ? <Spinner /> : 'Confirm'}</Button>
              <Button variant="ghost" onClick={() => setConfirmDel(false)}>Cancel</Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmDel(true)}>Delete</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminPromos() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-promos'], queryFn: api.adminListPlatformPromos });
  const { data: plansData } = useQuery({ queryKey: ['plans'], queryFn: api.listPlans });
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  const plans = plansData?.plans || [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = useMutation({
    mutationFn: () => {
      const isPlan = form.applicable.startsWith('plan:');
      const plan_id = isPlan ? form.applicable.slice(5) : null;
      const applicable_to = isPlan ? 'all' : form.applicable;
      let discount_value = Number(form.discount_value);
      if (form.discount_type === 'fixed') discount_value = Math.round(discount_value * 100); // rupees → paise
      return api.adminCreatePlatformPromo({
        code: form.code,
        discount_type: form.discount_type,
        discount_value,
        applicable_to,
        plan_id,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      });
    },
    onSuccess: () => { setForm(EMPTY); setError(''); qc.invalidateQueries({ queryKey: ['admin-promos'] }); },
    onError: (err) => setError(err.message),
  });

  if (isLoading) return <PageSkeleton />;
  const promos = data?.promo_codes || [];
  const canCreate = form.code.trim() && form.discount_value !== '' && !create.isPending;

  return (
    <div className="grid gap-6">
      {/* Create */}
      <Card>
        <h1 className="text-xl font-bold text-slate-900">Create Promotional Code</h1>
        <p className="mb-5 text-sm text-slate-500">Define promotional coupon discounts to share with hosts or clients.</p>

        <div className="grid gap-4 md:grid-cols-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Promo code</span>
            <Input placeholder="WELCOME20" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Discount type</span>
            <select value={form.discount_type} onChange={set('discount_type')} className="w-full rounded-dc-sm border border-slate-400 px-3 py-2 text-sm">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount (₹)</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Discount value</span>
            <Input type="number" step="0.01" min="0" placeholder="20.00" value={form.discount_value} onChange={set('discount_value')} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Max uses (optional)</span>
            <Input type="number" min="1" placeholder="100" value={form.max_uses} onChange={set('max_uses')} />
          </label>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Applicable to</span>
            <select value={form.applicable} onChange={set('applicable')} className="w-full rounded-dc-sm border border-slate-400 px-3 py-2 text-sm">
              <option value="all">All appointments</option>
              <option value="paid_appointments_only">Paid appointments only</option>
              {plans.map((pl) => <option key={pl.id} value={`plan:${pl.id}`}>{pl.name} Plan Upgrade</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium uppercase tracking-wide text-slate-500 text-xs">Expiration date (optional)</span>
            <Input type="date" value={form.expires_at} onChange={set('expires_at')} />
          </label>
        </div>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end">
          <Button onClick={() => create.mutate()} disabled={!canCreate}>{create.isPending ? <Spinner /> : 'Create Promo Code'}</Button>
        </div>
      </Card>

      {/* List */}
      <Card>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">All Promotional Codes</h2>
            <p className="text-sm text-slate-500">List of active coupon offers and discount rates.</p>
          </div>
          <Badge tone="neutral">{promos.length} Codes</Badge>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2 font-medium">Promo code</th>
                <th className="px-4 py-2 font-medium">Discount</th>
                <th className="px-4 py-2 font-medium">Applicable to</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Usage stats</th>
                <th className="px-4 py-2 font-medium">Expires at</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center italic text-slate-400">No promo codes created yet.</td></tr>
              ) : (
                promos.map((p) => <PromoRow key={p.id} p={p} />)
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
